import { FormEvent, useMemo, useState } from "react";

export default function CTA() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [company, setCompany] = useState("");
  const [note, setNote] = useState("");
  const [status, setStatus] = useState<
    "idle" | "submitting" | "success" | "error"
  >("idle");
  const [statusMessage, setStatusMessage] = useState("");

  const csrfToken = useMemo(() => {
    const tag = document.querySelector<HTMLMetaElement>(
      "meta[name='csrf-token']",
    );
    return tag?.content ?? "";
  }, []);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();

    if (status === "submitting") {
      return;
    }

    setStatus("submitting");
    setStatusMessage("");

    try {
      const response = await fetch("/request-access", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "X-CSRF-Token": csrfToken,
        },
        body: JSON.stringify({
          name: name.trim(),
          email: email.trim(),
          company: company.trim(),
          note: note.trim(),
        }),
      });

      const payload = (await response.json()) as {
        error?: string;
        message?: string;
      };
      if (!response.ok) {
        throw new Error(
          payload.error || "Unable to submit your request. Please try again.",
        );
      }

      setStatus("success");
      setStatusMessage(
        payload.message || "Request received. We'll be in touch soon.",
      );
      setName("");
      setEmail("");
      setCompany("");
      setNote("");
    } catch (error) {
      setStatus("error");
      setStatusMessage(
        error instanceof Error
          ? error.message
          : "Unable to submit your request. Please try again.",
      );
    }
  }

  return (
    <section
      id="contact"
      className="py-24 sm:py-32 border-t border-navy-700/60"
    >
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        {/* Outer glow wrapper */}
        <div className="relative mx-auto max-w-3xl rounded-2xl border border-navy-700/60 bg-gradient-to-b from-navy-800/80 to-navy-950 p-12 text-center overflow-hidden">
          {/* Background decoration */}
          <div className="absolute inset-0 -z-10 flex items-center justify-center">
            <div className="h-[400px] w-[600px] rounded-full bg-huskly-purple/10 blur-[100px]" />
          </div>

          <p className="text-sm font-semibold uppercase tracking-widest text-huskly-teal mb-4">
            Early Access
          </p>
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
            Be Among the First to Use Aegis Vol
          </h2>
          <p className="text-slate-400 leading-relaxed mb-8 max-w-xl mx-auto">
            Aegis Vol is currently in active development. We're selectively
            onboarding early access users — options traders, prop desks, and
            researchers who care about decision quality.
          </p>

          <form className="mx-auto max-w-md space-y-3" onSubmit={handleSubmit}>
            <input
              type="text"
              placeholder="Your name"
              value={name}
              onChange={(event) => setName(event.target.value)}
              required
              disabled={status === "submitting"}
              className="w-full rounded-md border border-navy-600 bg-navy-900 px-4 py-3 text-sm text-white placeholder-slate-500 focus:border-huskly-teal focus:outline-none focus:ring-1 focus:ring-huskly-teal disabled:opacity-60"
            />
            <input
              type="email"
              placeholder="your@email.com"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              required
              disabled={status === "submitting"}
              className="w-full rounded-md border border-navy-600 bg-navy-900 px-4 py-3 text-sm text-white placeholder-slate-500 focus:border-huskly-teal focus:outline-none focus:ring-1 focus:ring-huskly-teal disabled:opacity-60"
            />
            <input
              type="text"
              placeholder="Company (optional)"
              value={company}
              onChange={(event) => setCompany(event.target.value)}
              disabled={status === "submitting"}
              className="w-full rounded-md border border-navy-600 bg-navy-900 px-4 py-3 text-sm text-white placeholder-slate-500 focus:border-huskly-teal focus:outline-none focus:ring-1 focus:ring-huskly-teal disabled:opacity-60"
            />
            <textarea
              placeholder="What do you want to use Aegis Vol for? (optional)"
              value={note}
              onChange={(event) => setNote(event.target.value)}
              rows={3}
              disabled={status === "submitting"}
              className="w-full rounded-md border border-navy-600 bg-navy-900 px-4 py-3 text-sm text-white placeholder-slate-500 focus:border-huskly-teal focus:outline-none focus:ring-1 focus:ring-huskly-teal disabled:opacity-60"
            />
            <button
              type="submit"
              disabled={status === "submitting"}
              className="w-full rounded-full bg-huskly-green px-6 py-3 text-sm font-bold text-navy-950 hover:bg-huskly-green-light transition-colors disabled:cursor-not-allowed disabled:opacity-70"
            >
              {status === "submitting" ? "Submitting..." : "Request Access"}
            </button>

            {status !== "idle" && statusMessage && (
              <p
                className={`text-sm ${status === "success" ? "text-huskly-green-light" : "text-red-300"}`}
                role="status"
              >
                {statusMessage}
              </p>
            )}
          </form>

          <p className="mt-4 text-xs text-slate-600">
            No spam. No commitments. Just early access to the intelligence
            layer.
          </p>
        </div>
      </div>
    </section>
  );
}
